import { TreeSelect } from "antd";
import { Field, Form, Formik, FormikValues, setIn } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { deserialize, serialize } from "serializr";
import { MetaModel } from "../../../../../models/CourseModels/SubjectModel/subject.model";
import {
  BranchClassRoomsModel,
  BranchMetaModel,
} from "../../../../../models/InstituteModel/branches.model";
import { Institutes } from "../../../../../models/InstituteModel/institutes.model";
import {
  InstituteTutorCreateModel,
  InstituteTutorModel,
} from "../../../../../models/InstituteModel/tutor.model";
import BranchService from "../../../../../services/InstituteService/branch.service";
import SectionService from "../../../../../services/InstituteService/section.service";
import { InstituteTutorService } from "../../../../../services/InstituteService/tutor.service";
import DropdownField from "../../../../../shared/components/DropdownField";
import InputField from "../../../../../shared/components/InputField";
import ToggleSwitch from "../../../../../shared/components/ToggleSwitch";
import UIModal from "../../../../../shared/components/UIModal";
import UploadLogo from "../../../../../shared/components/UploadLogo";
import { convertJSONToFormData } from "../../../../../shared/utils/converJsonToFormData";
import { TeacherValidationSchema } from "../../../instituteFormValidation";
import "./instituteTeacherForm.scss";

interface InstituteTeacherFormProps {
  visible: boolean;
  closeHandler: () => void;
  data?: InstituteTutorModel;
  refreshList: () => void;
}

const InstituteTeacherForm: FC<InstituteTeacherFormProps> = (props) => {
  const { visible, closeHandler, data, refreshList } = props;
  const [initialValues, setInitialValues] = useState<any>();
  const formRef = useRef<any>();
  const [branches, setBranches] = useState<any>();
  const { state }: { state: Institutes } = useLocation();
  const [branchClassRooms, setBranchClassRooms] = useState<
    BranchClassRoomsModel[]
  >([]);
  const [branchClassRoomsMeta, setBranchClassRoomsMeta] = useState<MetaModel[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [expertise, setExpertise] = useState<any>();
  const params: any = useParams();
  const fetchBranches = (queryParams: any) => {
    BranchService.fetchBranches(
      { ...queryParams, institute_id: params?.instituteId },
      (data) => {
        let meta: any = deserialize(BranchMetaModel, data);
        setBranches([...meta]);
      },
      () => {},
      () => {}
    );
  };

  const fetchClassRooms = (id: any) => {
    SectionService.fetchBranchClassRooms(
      { branch_id: id },
      (classrooms: BranchClassRoomsModel[]) => {
        const meta = deserialize(MetaModel, classrooms) as MetaModel[];
        setBranchClassRoomsMeta(meta);
        setBranchClassRooms(classrooms);
        const options = classrooms.map((classroom) => ({
          title: classroom.name,
          value: classroom.id,
          key: classroom.id,
          type: "classroom",
          children: classroom?.sections?.map((section) => ({
            title: section.name,
            value: classroom.id + "-" + section.id,
            key: classroom.id + "-" + section.id,
            type: "section",
            children: section?.sectionSubjects?.map((subject) => ({
              title: subject.name,
              value: classroom.id + "-" + section.id + "-" + subject.id,
              key: classroom.id + "-" + section.id + "-" + subject.id,
              type: "subject",
            })),
          })),
        }));
        setExpertise(options);
      },
      () => {},
      () => {}
    );
  };

  useEffect(() => {
    if (data?.branch?.id) {
      fetchClassRooms(data?.branch?.id);
    }
    setInitialValues({
      fullName: data?.fullName,
      phoneNumber: data?.phoneNumber,
      email: data?.email,
      branchId: data?.branch?.id,
      expertise: data?.tutorClassRooms?.flatMap((classroom) => {
        return classroom?.tutorSections?.flatMap((section) => {
          return section?.tutorSubjects?.flatMap((subject) => {
            return classroom?.id + "-" + section?.id + "-" + subject?.id;
          });
        });
      }),
      branchClassRoomId: data?.tutorClassRooms?.filter(
        (data) => data?.classTeacher
      )?.[0]?.id,
      isActive: data ? data?.isActive : true,
      displayPictureUrl: data?.displayPictureUrl,
    });
  }, [data]);

  const onSubmit = (values: FormikValues) => {
    setLoading(true);
    //if class is selected (1), traverse through sections and return all subjects
    //if section is selected (1-1), match classroom-section and return subjects
    //if subject is selected (1-1-1), match classroom-section-subject and return

    const sectionSubjects = values?.expertise?.flatMap((value) => {
      return branchClassRooms?.flatMap((classroom) => {
        if (
          classroom?.sections?.length &&
          ((Number.isInteger(value) && classroom?.id == value) ||
            (!Number.isInteger(value) &&
              classroom?.id == value?.split("-")?.[0]))
        ) {
          return classroom?.sections?.flatMap((section) => {
            if (
              section?.sectionSubjects?.length &&
              (Number.isInteger(value) || section?.id == value?.split("-")?.[1])
            ) {
              return section?.sectionSubjects?.flatMap((subject) => {
                if (
                  Number?.isInteger(value) ||
                  subject?.id == value?.split("-")?.[2] ||
                  value?.split("-")?.length == 2
                ) {
                  return subject?.id;
                }
              });
            }
          });
        }
      });
    });

    const jsonPayload = serialize(InstituteTutorCreateModel, {
      ...values,
      expertise: sectionSubjects?.filter((data) => data),
    });
    if (values?.displayPicture) {
      jsonPayload["display_picture"] = values?.displayPicture;
    }
    if(!values?.branchClassRoomId) {
      delete jsonPayload["branch_class_rooms"]
    }
    const formDataPayload = convertJSONToFormData({ tutor: jsonPayload }, true);
    if (data?.id) {
      InstituteTutorService.updateInstituteTutor(
        data?.id,
        formDataPayload,
        () => {
          formRef.current.resetForm({ values: null });
          closeHandler();
          refreshList();
        },
        () => {},
        () => {
          setLoading(false);
        }
      );
    } else {
      InstituteTutorService.createInstituteTutor(
        formDataPayload,
        () => {
          formRef.current.resetForm({ values: null });
          closeHandler();
          refreshList();
        },
        () => {},
        () => {
          setLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    fetchBranches({});
  }, []);
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      innerRef={formRef}
      validationSchema={TeacherValidationSchema}
    >
      {({ values, setFieldValue, resetForm, handleSubmit }) => (
        <UIModal
          visible={visible}
          closeModal={closeHandler}
          onClosed={resetForm}
          submitProps={{
            text: data ? "Update" : "Create",
            clickHandler: handleSubmit,
            buttonLoading: loading,
          }}
          cancelProps={{
            text: "Cancel",
            clickHandler: closeHandler,
          }}
          className="institute-teacher-form"
        >
          <Form placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <div className="institute-teacher-form__header">
              <div className="institute-teacher-form__title">
                {data ? "Update Teacher" : "Create New Teacher"}
              </div>
              <div className="institute-teacher-form__toggle">
                <Field name="isActive" as="switch">
                  <ToggleSwitch
                    checked={values?.isActive}
                    handleChange={(checked) => {
                      setFieldValue("isActive", checked);
                    }}
                  />
                </Field>
              </div>
            </div>
            <Field name="logo" as="file-upload">
              <UploadLogo
                logoUrl={
                  values?.displayPictureUrl
                    ? values?.displayPictureUrl
                    : data?.displayPictureUrl
                }
                setFile={(_, file) => {
                  setFieldValue("displayPicture", file);
                  setFieldValue("displayPictureUrl", URL.createObjectURL(file));
                }}
              />
            </Field>
            <div className="institute-teacher-form__input">
              <div className="institute-teacher-form__label">Teacher Name</div>
              <InputField type="text" name="fullName" placeholder="Enter" />
            </div>
            <div className="institute-teacher-form__input">
              <div className="institute-teacher-form__label">Phone Number</div>
              <InputField
                type="text"
                name="phoneNumber"
                placeholder="Enter"
                prefix={"+91"}
              />
            </div>
            <div className="institute-teacher-form__input">
              <div className="institute-teacher-form__label">Email ID</div>
              <InputField type="text" name="email" placeholder="Enter" />
            </div>
            <div className="institute-teacher-form__input">
              <div className="institute-teacher-form__label">Branch</div>
              <DropdownField
                name="branchId"
                placeholder="Select"
                options={branches}
                value={values?.branchId}
                onChange={(value) => {
                  setFieldValue("branchId", value);
                  fetchClassRooms(value);
                }}
                showSearch={false}
                optionFilterProp="label"
              />
            </div>
            <div className="institute-teacher-form__input expertise-field">
              <div className="institute-teacher-form__label">Expertise</div>
              <Field as="tree-select" name="expertise">
                <TreeSelect
                  style={{ width: "100%" }}
                  value={values?.expertise}
                  dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                  treeData={expertise}
                  placeholder="Search"
                  allowClear
                  multiple
                  treeCheckable={true}
                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                  onChange={(value) => {
                    setFieldValue("expertise", value);
                    console.log(value);
                  }}
                />
              </Field>
            </div>
            <div className="institute-teacher-form__input">
              <div className="institute-teacher-form__label">Class Teacher</div>
              <DropdownField
                name="branchClassRoomId"
                placeholder="Select"
                options={branchClassRoomsMeta}
                value={values?.branchClassRoomId}
                onChange={(value) => {
                  setFieldValue("branchClassRoomId", value);
                }}
                showSearch={false}
                optionFilterProp="label"
              />
            </div>
            {!data ? (
              <div className="institute-teacher-form__input">
                <div className="institute-teacher-form__label">Password</div>
                <InputField type="text" name="password" placeholder="Enter" />
              </div>
            ) : (
              ""
            )}
          </Form>
        </UIModal>
      )}
    </Formik>
  );
};

export default InstituteTeacherForm;
